import React, { memo } from 'react';
import { Carrot, Package, Snowflake } from 'lucide-react';

import { Progress } from 'shadcn/components/ui/Progress';
import { cn } from 'shadcn/lib/utils';

/**
 * Box type configuration
 */
const BOX_TYPE_CONFIG = {
    regular: {
        label: 'Regular',
        Icon: Carrot,
        iconClassName: 'text-orange-500',
    },
    frozen: {
        label: 'Frozen',
        Icon: Snowflake,
        iconClassName: 'text-blue-500',
    },
    oob: {
        label: 'OOB',
        Icon: Package,
        iconClassName: 'text-purple-500',
    },
};

/**
 * ProgressBreakdown - Displays progress bars for box type breakdown
 *
 * @param {Object} props
 * @param {Array} [props.breakdowns] - Array of box type breakdowns
 * @param {number} [props.labelsCount] - Aggregate labels count (fallback)
 * @param {number} [props.packedCount] - Aggregate packed count (fallback)
 * @param {number} [props.transitCount] - Aggregate transit count (fallback)
 * @param {number} [props.totalShipments] - Total shipments (fallback)
 */
function ProgressBreakdown({ breakdowns, labelsCount, packedCount, totalShipments, transitCount }) {
    // Use aggregate mode if breakdowns is empty but aggregate data provided
    const hasBreakdowns = breakdowns && breakdowns.length > 0;
    const hasAggregateData = totalShipments !== undefined && totalShipments > 0;

    if (!hasBreakdowns && !hasAggregateData) {
        return null;
    }

    // Aggregate mode: single row with aggregate counts
    if (!hasBreakdowns && hasAggregateData) {
        return (
            <div className="space-y-2">
                <BoxTypeRow
                    type="aggregate"
                    labels={labelsCount ?? 0}
                    packed={packedCount ?? 0}
                    onTruck={transitCount ?? 0}
                    total={totalShipments}
                    showHeaders
                    hideLabel
                />
            </div>
        );
    }

    // Breakdown mode: one row per box type
    return (
        <div className="space-y-2">
            {breakdowns.map((breakdown, index) => (
                <BoxTypeRow
                    key={breakdown.type}
                    type={breakdown.type}
                    labels={breakdown.labels}
                    packed={breakdown.packed}
                    onTruck={breakdown.onTruck}
                    total={breakdown.total}
                    showHeaders={index === 0}
                />
            ))}
        </div>
    );
}

/**
 * Individual box type row with progress bars
 */
function BoxTypeRow({ hideLabel = false, labels, onTruck, packed, showHeaders = false, total, type }) {
    const config = BOX_TYPE_CONFIG[type] || { label: type, Icon: Package, iconClassName: 'text-gray-500' };
    const { Icon, iconClassName, label } = config;

    const getProgressPercent = (count) => (total > 0 && Number.isFinite(count) ? (count / total) * 100 : 0);

    return (
        <div className="space-y-1">
            {/* Header row with column labels */}
            {showHeaders && (
                <div className="grid grid-cols-4 gap-2 text-xs text-muted-foreground">
                    <div>{/* Box type label column */}</div>
                    <div>Labels</div>
                    <div>Packed</div>
                    <div>On Truck</div>
                </div>
            )}

            {/* Data row */}
            <div className="grid grid-cols-4 gap-2 items-center">
                {/* Box type label with icon */}
                {!hideLabel && (
                    <div className="flex items-center gap-1.5">
                        <Icon className={cn('h-4 w-4', iconClassName)} />
                        <span className="text-sm font-medium">{label}</span>
                    </div>
                )}
                {hideLabel && <div />}

                {/* Labels progress */}
                <ProgressCell count={labels} total={total} percent={getProgressPercent(labels)} />

                {/* Packed progress */}
                <ProgressCell count={packed} total={total} percent={getProgressPercent(packed)} />

                {/* On Truck progress */}
                <ProgressCell count={onTruck} total={total} percent={getProgressPercent(onTruck)} />
            </div>
        </div>
    );
}

/**
 * Progress bar cell with count
 */
function ProgressCell({ count, percent, total }) {
    const safeCount = Number.isFinite(count) ? count : 0;
    return (
        <div className="space-y-0.5">
            <div className="text-xs text-muted-foreground">
                {safeCount}/{total}
            </div>
            <Progress value={percent} className="h-1.5" />
        </div>
    );
}

export default memo(ProgressBreakdown);
